import { CSSProperties, useEffect, useState } from 'react';
import {
    CampaignStatus,
    CampaignStatuses,
    MatrixCampaignType,
    MatrixCampaignTypes,
} from '../../../../types/enums/campaign-enums';
import {
    Card,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { MatrixTableHeader, MatrixTableRow } from '../../types/interfaces/CampaingTypes';
import { Circle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getMatrixList } from '../../../../stores/redux/actions';
import Helper from '../../../../utils/helper';
import { emptyCampaign } from '../../../../assets/images';

export const SCUIMatrixTable = () => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const regionStore = useSelector((state: any) => state.region);
    const matrixStore = useSelector((state: any) => state.campaignMatrixList);
    const campaignsStore = useSelector((state: any) => state.campaignMatrixCampaigns);
    const liveCampaignStore = useSelector((state: any) => state.campaignLiveCampaign);

    const [dimensions, setDimensions] = useState({
        maxHeight: window.innerHeight - 220,
        maxWidth: window.innerWidth - 220,
    });

    const [columnHeaders, setColumnHeaders] = useState<MatrixTableHeader[]>([]);
    const [rowHeaders, setRowHeaders] = useState<MatrixTableHeader[]>([]);

    const [matrixRows, setMatrixRows] = useState<MatrixTableRow[]>([]);

    const getHeaders = () => {
        const liveCampaignStatus = liveCampaignStore.data;
        const campaigns = campaignsStore.data;

        const conditionStatuses: number[] =
            liveCampaignStatus === -1 ? CampaignStatuses.map((x: any) => x.id) : [CampaignStatus.Active];

        const isSelectedCampaignExist = campaigns.filter((y: any) => y.selected).length > 0;

        const _headers = matrixStore.data?.campaignRules
            .filter((x: any) => conditionStatuses.includes(x.status))
            .map((rule: any) => {
                return {
                    id: rule.id,
                    name: rule.title,
                    status: rule.status,
                    processOrder: rule.processOrder,
                    xHide: isSelectedCampaignExist ? !campaigns?.some((x: any) => x.id == rule.id && x.selected) : true,
                    yHide: false,
                };
            });

        const columnHeaders = JSON.parse(JSON.stringify(_headers))
            .filter((x: any) => !x.xHide)
            .sort((a: any, b: any) => {
                return b.processOrder - a.processOrder;
            });

        const rowHeaders = JSON.parse(JSON.stringify(_headers))
            .filter((x: any) => !x.yHide)
            .sort((a: any, b: any) => {
                return a.processOrder - b.processOrder;
            });

        if (matrixStore.data?.campaignRules.length > 0) {
            setColumnHeaders(columnHeaders);
            setRowHeaders(rowHeaders);
        }

        return _headers;
    };

    const getRows = () => {
        const _rows = matrixStore.data?.campaignRuleMatrixes.map((matrix: any) => {
            return {
                id: matrix.id,
                ruleA: matrix.campaignRuleA,
                ruleB: matrix.campaignRuleB,
                isCombinable: matrix.isCombinable,
                show: false,
            };
        });

        if (matrixStore.data?.campaignRuleMatrixes.length > 0) {
            setMatrixRows(_rows);
        }

        return _rows;
    };

    useEffect(() => {
        getHeaders();
        getRows();
    }, [matrixStore, liveCampaignStore, campaignsStore, dimensions, regionStore?.region]);

    const applyFiltered = columnHeaders.length != rowHeaders.length;

    useEffect(() => {
        dispatch(getMatrixList(Number(regionStore?.region)));
    }, [dispatch, regionStore?.region]);

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                maxHeight: window.innerHeight - 220,
                maxWidth: window.innerWidth - 220,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getMatrixValue = (ruleAId: number, ruleBId: number) => {
        const entries = matrixRows.filter(
            (m) =>
                ((m.ruleA === ruleAId && m.ruleB === ruleBId) || (m.ruleA === ruleBId && m.ruleB === ruleAId)) &&
                !m.show
        );

        const entry = entries.length > 0 ? entries[0] : null;

        let isShow = false;
        let value = { title: '', shortKey: '' };
        switch (entry?.isCombinable) {
            case MatrixCampaignType.DontMerge:
                value = {
                    title: t(MatrixCampaignTypes.DontMerge.title),
                    shortKey: t(MatrixCampaignTypes.DontMerge.shortKey),
                };
                isShow = true;
                break;
            case MatrixCampaignType.Merged:
                value = {
                    title: t(MatrixCampaignTypes.Merged.title),
                    shortKey: t(MatrixCampaignTypes.Merged.shortKey),
                };
                isShow = true;
                break;
            case MatrixCampaignType.DontApplySameProducts:
                value = {
                    title: t(MatrixCampaignTypes.DontApplySameProducts.title),
                    shortKey: t(MatrixCampaignTypes.DontApplySameProducts.shortKey),
                };
                isShow = true;
                break;
            default:
                return value;
        }

        if (isShow) {
            entries.map((e) => (e.show = true));
        }

        return value;
    };

    const MatrixTableContainer = styled(TableContainer)(
        ({ maxHeight, maxWidth }: { maxHeight: number; maxWidth: number }) => ({
            maxHeight,
            maxWidth,
            '& table': {
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
            },
            '& thead th': {
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 2,
                fontSize: '2.2rem!important',
                minWidth: '100px!important',
                maxWidth: '264px!important',
                height: '42px!important',
                border: '1px solid #e0e0e0',
            },
            '& table th': {
                alignContent: 'center',
            },
            '& th a': {
                fontWeight: 'bold',
                // lineHeight: 2,
            },
            '& tbody th': {
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                zIndex: 1,
                fontSize: '2.2rem!important',
                minWidth: '100px!important',
                maxWidth: '120px!important',
                height: '42px!important',
                border: '1px solid #e0e0e0',
            },
            '& td': {
                fontSize: '1.4rem!important',
                border: '1px solid #e0e0e0',
            },
        })
    );

    const MatrixCircle = styled(Circle)({
        height: 12,
        width: 12,
        fontSize: '12px',
        marginRight: '8px',
    });

    const MatrixHeadCell = ({ data, style }: { data: any; style?: CSSProperties }) => {
        const CellSpan = styled(Link)({
            textDecoration: 'none',
            color: 'black',
            '&:hover': {
                textDecoration: 'underline',
                cursor: 'pointer',
            },
        });

        const handleClick = () => {
            Helper.openInNewTab(`/campaign/campaignmanagement/save/${data.id}`);
        };

        return (
            <TableCell component="th" style={style}>
                <Tooltip
                    title={t(CampaignStatuses.find((x: any) => x.id == data.status)?.name ?? '')}
                    style={{ fontSize: '16px!important' }}
                >
                    <MatrixCircle
                        style={{
                            color: CampaignStatuses.find((x: any) => x.id == data.status)?.color,
                        }}
                    />
                </Tooltip>

                <CellSpan variant="h6" onClick={handleClick}>{`${data.id} - ${data.name}`}</CellSpan>
            </TableCell>
        );
    };

    const MatrixTableRow = ({ index, row, cells }: { index: number; row: any; cells: MatrixTableHeader[] }) => {
        return (
            <TableRow key={row.id}>
                <MatrixHeadCell key={row.id} data={row} />

                {cells.map((cell: any, colIndex: number) => {
                    const matrixValue = getMatrixValue(row.id, cell.id);
                    const check = applyFiltered ? colIndex < cells.length : colIndex < cells.length - index;

                    return check ? (
                        <TableCell key={cell.id} align="center">
                            <Tooltip title={matrixValue.title} style={{ fontSize: '16px!important' }}>
                                <span>{matrixValue.shortKey}</span>
                            </Tooltip>
                        </TableCell>
                    ) : (
                        <TableCell key={cell.id}></TableCell>
                    );
                })}
            </TableRow>
        );
    };

    const Item = styled(Card)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
    }));

    const NoRecord = () => {
        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                }}
                style={{ textAlign: 'center' }}
            >
                <img src={emptyCampaign} style={{ maxHeight: '250px' }} />
                <Typography style={{ margin: '20px' }}>{t('campaignManagement.matrix.noRecordMessage')}</Typography>
            </Item>
        );
    };

    return (
        <Paper>
            {campaignsStore && campaignsStore.data?.filter((y: any) => y.selected).length > 0 ? (
                <MatrixTableContainer maxHeight={dimensions.maxHeight} maxWidth={dimensions.maxWidth}>
                    <Table size="small" aria-label="campaign matrix table">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                {columnHeaders.map((header: any) => (
                                    <MatrixHeadCell key={header.id} data={header} style={{ textAlign: 'center' }} />
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rowHeaders.map((headerX: { [key: string]: any }, rowIndex: number) => {
                                return (
                                    <MatrixTableRow
                                        key={rowIndex}
                                        index={rowIndex}
                                        row={headerX}
                                        cells={columnHeaders}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </MatrixTableContainer>
            ) : (
                <NoRecord />
            )}
        </Paper>
    );
};
